import {
  ArrowLeft,
  RefreshCw,
  AlertCircle,
  Eye,
  Ban,
  RotateCcw,
  Loader2,
  Sparkles,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { ApiError } from "@/lib/api/client";
import type { InvoiceFileOut } from "@/lib/api/jobs";
import { cn } from "@/lib/cn";
import { useAuthStore } from "@/features/auth/store";
import {
  AUTOMATION_SUMMARY_KEYS,
  summarizeAutomation,
} from "@/features/records/automationStatusUtil";

import CsvDownloadButtons from "./CsvDownloadButtons";
import RecordTable from "./RecordTable";
import StatusBadge from "./StatusBadge";
import { formatDateTime, progressPercent } from "./format";
import { useCancelJob, useRetryJob } from "./useJobActions";
import { useJobDetail } from "./useJobDetail";

export default function JobDetailPage() {
  const { t } = useTranslation();
  const params = useParams<{ id: string }>();
  const jobId = params.id;
  const { data: job, isLoading, isError, error, refetch, isFetching } = useJobDetail(jobId);
  const currentUser = useAuthStore((s) => s.user);
  // 관리자 감독 조회: 내 작업이 아니면 읽기 전용(편집/검수/재처리 잠금).
  const readOnly = !!job && !!currentUser && job.user_id !== currentUser.id;

  const cancel = useCancelJob();
  const retry = useRetryJob();
  // 진행 중(pending/processing)은 중지, 중지/실패는 재시도 가능(읽기전용 감독 시 숨김).
  const canCancel = !!job && !readOnly && ["pending", "processing"].includes(job.status);
  const isCancelling = !!job && job.status === "cancelling";
  const canRetry = !!job && !readOnly && ["failed", "cancelled"].includes(job.status);

  function handleCancel(): void {
    if (!jobId || !window.confirm(t("jobs.detail.actions.cancelConfirm"))) return;
    cancel.mutate(jobId);
  }
  function handleRetry(): void {
    if (!jobId) return;
    retry.mutate(jobId);
  }

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      {/* 상단 네비 */}
      <div className="flex items-center justify-between">
        <Link
          to="/jobs"
          className="inline-flex items-center gap-1 text-xs text-slate-500 transition hover:text-slate-800"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          {t("jobs.detail.backToList")}
        </Link>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-2.5 py-1 text-[11px] text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
        >
          <RefreshCw className={cn("h-3 w-3", isFetching && "animate-spin")} />
          {t("common.actions.refresh")}
        </button>
      </div>

      {isLoading ? (
        <SkeletonHeader />
      ) : isError ? (
        <ErrorBox error={error} />
      ) : !job ? null : (
        <>
          {/* 관리자 감독(읽기전용) 안내 배너 */}
          {readOnly && (
            <div className="flex items-center gap-2 rounded-md border border-indigo-200 bg-indigo-50 px-4 py-2.5 text-xs text-indigo-800">
              <Eye className="h-4 w-4 flex-shrink-0" />
              <span>{t("jobs.detail.readOnlyBanner")}</span>
            </div>
          )}

          {/* 헤더 카드 */}
          <header className="rounded-lg border border-slate-200 bg-white p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h1 className="text-lg font-semibold text-slate-900">
                  {job.batch_name || (
                    <span className="text-slate-400">{t("common.placeholders.anonymous")}</span>
                  )}
                </h1>
                <p className="mt-0.5 text-[11px] text-slate-400">#{job.id}</p>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={job.status} size="md" />
                {(canCancel || isCancelling) && !readOnly && (
                  <button
                    type="button"
                    onClick={handleCancel}
                    disabled={cancel.isPending || isCancelling}
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-md border border-amber-300 px-2.5 py-1 text-[11px] font-medium text-amber-700 transition hover:bg-amber-50",
                      (cancel.isPending || isCancelling) && "cursor-not-allowed opacity-60",
                    )}
                  >
                    {cancel.isPending || isCancelling ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Ban className="h-3 w-3" />
                    )}
                    {isCancelling
                      ? t("jobs.detail.actions.cancelling")
                      : t("jobs.detail.actions.cancel")}
                  </button>
                )}
                {canRetry && (
                  <button
                    type="button"
                    onClick={handleRetry}
                    disabled={retry.isPending}
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-md border border-blue-300 px-2.5 py-1 text-[11px] font-medium text-blue-700 transition hover:bg-blue-50",
                      retry.isPending && "cursor-not-allowed opacity-60",
                    )}
                  >
                    {retry.isPending ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <RotateCcw className="h-3 w-3" />
                    )}
                    {t("jobs.detail.actions.retry")}
                  </button>
                )}
              </div>
            </div>
            {(cancel.isError || retry.isError) && (
              <p
                role="alert"
                className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-[11px] text-red-700"
              >
                {(() => {
                  const e = cancel.error ?? retry.error;
                  return e instanceof ApiError ? e.detail : t("jobs.detail.actions.actionError");
                })()}
              </p>
            )}

            <dl className="mt-4 grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
              <Stat
                label={t("jobs.detail.stats.fileCount")}
                value={t("jobs.list.fileCount", { count: job.file_count })}
              />
              <Stat
                label={t("jobs.detail.stats.success")}
                value={`${job.success_count}`}
                tone="green"
              />
              <Stat
                label={t("jobs.detail.stats.rejected")}
                value={`${job.rejected_count}`}
                tone="amber"
              />
              <Stat label={t("jobs.detail.stats.error")} value={`${job.error_count}`} tone="red" />
            </dl>

            {job.auto_review && <AutomationSummary files={job.files} />}

            {/* 진행률 (처리 중일 때만) */}
            {(job.status === "pending" || job.status === "processing") && (
              <div className="mt-4">
                <div className="mb-1 flex justify-between text-[11px] text-slate-500">
                  <span>{t("jobs.detail.progress")}</span>
                  <span>
                    {progressPercent(
                      job.file_count,
                      job.success_count,
                      job.rejected_count,
                      job.error_count,
                    )}
                    %
                  </span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
                  <div
                    className="h-full bg-blue-500 transition-[width] duration-500"
                    style={{
                      width: `${progressPercent(
                        job.file_count,
                        job.success_count,
                        job.rejected_count,
                        job.error_count,
                      )}%`,
                    }}
                  />
                </div>
              </div>
            )}

            <div className="mt-4 grid grid-cols-1 gap-1 text-[11px] text-slate-500 sm:grid-cols-3">
              <span>
                {t("jobs.detail.timestamps.created")} {formatDateTime(job.created_at)}
              </span>
              <span>
                {t("jobs.detail.timestamps.started")} {formatDateTime(job.started_at)}
              </span>
              <span>
                {t("jobs.detail.timestamps.completed")} {formatDateTime(job.completed_at)}
              </span>
            </div>
          </header>

          {/* CSV 다운로드 */}
          <CsvDownloadButtons
            jobId={job.id}
            successCount={job.success_count}
            rejectedCount={job.rejected_count}
            files={job.files}
          />

          {/* 결과 테이블 */}
          <RecordTable jobId={job.id} files={job.files} readOnly={readOnly} />
        </>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "green" | "amber" | "red";
}) {
  const toneCls =
    tone === "green"
      ? "text-green-700"
      : tone === "amber"
        ? "text-amber-700"
        : tone === "red"
          ? "text-red-700"
          : "text-slate-900";
  return (
    <div className="rounded-md border border-slate-100 bg-slate-50 px-3 py-2">
      <p className="text-[10px] uppercase tracking-wide text-slate-400">{label}</p>
      <p className={cn("mt-0.5 text-lg font-semibold tabular-nums", toneCls)}>{value}</p>
    </div>
  );
}

/** M26 — 자동 검수 묶음별 건수 (자동 검수를 켠 작업만). */
function AutomationSummary({ files }: { files: InvoiceFileOut[] }) {
  const { t } = useTranslation();
  const counts = summarizeAutomation(files);
  return (
    <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-violet-100 bg-violet-50/60 px-3 py-2 text-[11px] text-slate-600">
      <span className="inline-flex items-center gap-1 font-medium text-violet-700">
        <Sparkles className="h-3.5 w-3.5" />
        {t("jobs.detail.automation.title")}
      </span>
      {AUTOMATION_SUMMARY_KEYS.map((key) => (
        <span
          key={key}
          className={cn(
            counts[key] === 0
              ? "text-slate-400"
              : (key === "needsHuman" || key === "autoFixPending") && "font-medium text-amber-700",
          )}
        >
          {t(`jobs.detail.automation.${key}`)}{" "}
          <span className="font-semibold tabular-nums">{counts[key]}</span>
        </span>
      ))}
    </div>
  );
}

function SkeletonHeader() {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5">
      <div className="h-5 w-1/3 animate-pulse rounded bg-slate-100" />
      <div className="mt-2 h-3 w-1/4 animate-pulse rounded bg-slate-100" />
      <div className="mt-4 grid grid-cols-4 gap-3">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-12 animate-pulse rounded bg-slate-100" />
        ))}
      </div>
    </div>
  );
}

function ErrorBox({ error }: { error: unknown }) {
  const msg = error instanceof ApiError ? error.detail : (error as Error).message;
  return (
    <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
      <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
      <span>{msg}</span>
    </div>
  );
}
